import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FeedbackLoadingSpinner,
  HandoverDetailRecord,
  HandoverDetailView,
  SideBarModal,
} from '@/components';
import type { HandoverLeg } from '@/components';
import { RentalDetail } from '@/types/rental';
import { getHandoverById } from '@/services/api/handovers';
import { extractApiErrorMessage } from '@/utils';
import { formatDate, formatVehicleLabel } from '../rentalDisplay';

interface HandoverInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  detail: RentalDetail;
  /** Nothing renders until a leg is picked. */
  leg: HandoverLeg | null;
}

/**
 * The rental's handover evidence, read from the handover itself.
 *
 * A rent points at a handover; it does not own one. So rather than re-render
 * the trimmed copy the rental payload carries, this fetches the canonical row
 * and hands it to the same `HandoverDetailView` the handover screen uses. The
 * rental copy is still good enough for the summary card that opens this.
 *
 * The two cards on the rent detail screen are two LEGS of that one row, not two
 * records — so this passes the leg down and the view renders only that half.
 */
const HandoverInfoModal: React.FC<HandoverInfoModalProps> = ({
  isOpen,
  onClose,
  detail,
  leg,
}) => {
  const handoverId = detail.handover?.ID ?? null;

  const [record, setRecord] = useState<HandoverDetailRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!handoverId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getHandoverById(handoverId);
      setRecord((response as HandoverDetailRecord) ?? null);
    } catch (err) {
      setRecord(null);
      setError(extractApiErrorMessage(err, 'Gagal memuat detail handover'));
    } finally {
      setIsLoading(false);
    }
  }, [handoverId]);

  // Fetch on open, but only once per handover id: both legs read the same
  // row, so Handover -> close -> Return used to issue two identical GETs.
  // A closed modal never hits the endpoint at all — the card face already
  // carries what the summary needs.
  const loadedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen || !handoverId) return;
    if (loadedIdRef.current === handoverId && record) return;
    loadedIdRef.current = handoverId;
    load();
  }, [isOpen, handoverId, record, load]);

  if (!leg || !handoverId) return null;

  const isReturn = leg === 'RETURN';
  const legDate = isReturn
    ? detail.handover?.returnDate
    : detail.handover?.handoverDate;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      width="full"
      position="center"
      header={
        <div>
          <h2 className="text-[18px] font-medium text-gray-900">
            {isReturn ? 'Detail Pengembalian' : 'Detail Handover'}
          </h2>
          <p className="text-[13px] text-gray-500 mt-0.5">
            {[
              detail.vehicle?.platNomor,
              formatVehicleLabel(detail.vehicle),
              formatDate(legDate, ''),
            ]
              .filter(Boolean)
              .join(' • ') || '-'}
          </p>
        </div>
      }
    >
      <div className="p-6">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <FeedbackLoadingSpinner size="large" />
            <span className="ml-2 text-[14px] text-gray-600">
              Memuat detail handover...
            </span>
          </div>
        )}

        {!isLoading && error && (
          <div className="py-16 text-center">
            <p className="text-[14px] text-gray-500">{error}</p>
            <button
              type="button"
              onClick={load}
              className="mt-3 text-[14px] font-medium text-primary underline"
            >
              Coba lagi
            </button>
          </div>
        )}

        {!isLoading && !error && !record && (
          <p className="py-16 text-center text-[14px] text-gray-400">
            Detail handover tidak ditemukan.
          </p>
        )}

        {/* Scoped to the leg that was clicked. One row holds both, so an
            unscoped view showed the return's odometer, photos and BAST to
            someone who had asked about the pickup. */}
        {!isLoading && !error && record && (
          <HandoverDetailView handover={record} leg={leg} />
        )}
      </div>
    </SideBarModal>
  );
};

export default HandoverInfoModal;
